/* aviaf — WebSocket bridge to /ws.
 *
 * Per snuggly-popping-sparrow.md §5: never put the long-lived session JWT
 * in a URL. Instead, fetch a 60-second ws-purpose ticket via the cookie-
 * protected REST endpoint and use that as ?token=. The backend's
 * authorize_ws_token() requires payload.purpose === "ws", so a leaked
 * session JWT is unusable on the WS endpoint.
 *
 * Usage:
 *   const ws = await window.openHubSocket();
 *   ws.addEventListener("message", (e) => handle(JSON.parse(e.data)));
 *   ws.addEventListener("close", reconnectAfterBackoff);
 */

(function () {
  async function fetchWsTicket() {
    const r = await fetch("/api/auth/ws-ticket", {
      method: "POST",
      credentials: "same-origin",
    });
    if (!r.ok) {
      throw new Error(`ws-ticket: ${r.status}`);
    }
    const body = await r.json();
    return body.token;
  }

  async function openHubSocket() {
    let ticket;
    try {
      ticket = await fetchWsTicket();
    } catch (err) {
      // Auth disabled? /api/auth/ws-ticket still 200s with token "dev",
      // so any error here is a real failure (not the disabled-auth case).
      // Surface to the caller; reconnect logic handles backoff.
      throw err;
    }
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${location.host}/ws?token=${encodeURIComponent(ticket)}`;
    return new WebSocket(url);
  }

  /** Auto-reconnecting bridge. Pass an onMessage callback. Returns
   *  { close, forceReconnect, lastFrameMs } so the caller can shut it
   *  down on unmount, force a reconnect on visibility/online events,
   *  and inspect liveness.
   *
   *  Half-open detection: the server never sends application-level pings,
   *  and a half-open TCP (laptop sleep, wifi flap, NAT timeout) leaves
   *  `ws.readyState === OPEN` so onclose never fires — the dashboard
   *  silently freezes.  We watchdog the inbound stream: if no frame has
   *  arrived for STALE_MS, close the socket so the reconnect path runs.
   *  The hub-side pubsub is chatty enough (equity/health/heartbeat) that
   *  any healthy connection sees at least one frame every few seconds. */
  const STALE_MS = 45_000;
  const WATCHDOG_INTERVAL_MS = 5_000;

  function createHubBridge({ onMessage, onStatus }) {
    let ws = null;
    let stopped = false;
    let attempt = 0;
    let lastFrameMs = Date.now();
    let watchdogTimer = null;
    const BACKOFFS_MS = [500, 1000, 2000, 4000, 8000, 15000];

    function killStaleSocket() {
      // Force the socket closed.  The close listener triggers schedule(),
      // which kicks off the normal reconnect → bootstrap path.
      try { ws && ws.close(); } catch (_) {}
    }

    function startWatchdog() {
      if (watchdogTimer) return;
      watchdogTimer = setInterval(() => {
        if (stopped) return;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        if (Date.now() - lastFrameMs > STALE_MS) killStaleSocket();
      }, WATCHDOG_INTERVAL_MS);
    }
    function stopWatchdog() {
      if (watchdogTimer) { clearInterval(watchdogTimer); watchdogTimer = null; }
    }

    async function connect() {
      if (stopped) return;
      try {
        onStatus && onStatus("connecting");
        ws = await openHubSocket();
        ws.addEventListener("open", () => {
          attempt = 0;
          lastFrameMs = Date.now();
          startWatchdog();
          onStatus && onStatus("live");
        });
        ws.addEventListener("message", (e) => {
          lastFrameMs = Date.now();
          try { onMessage(JSON.parse(e.data)); }
          catch (_) { /* ignore malformed frame */ }
        });
        ws.addEventListener("close", () => {
          stopWatchdog();
          if (stopped) return;
          onStatus && onStatus("reconnecting");
          schedule();
        });
        ws.addEventListener("error", () => {
          // close handler also fires; let it drive reconnect.
        });
      } catch (err) {
        if (stopped) return;
        onStatus && onStatus("reconnecting");
        schedule();
      }
    }

    function schedule() {
      const delay = BACKOFFS_MS[Math.min(attempt, BACKOFFS_MS.length - 1)];
      attempt += 1;
      setTimeout(connect, delay);
    }

    connect();

    return {
      close() {
        stopped = true;
        stopWatchdog();
        if (ws && ws.readyState === WebSocket.OPEN) ws.close();
      },
      // Caller-triggered reconnect — used on visibilitychange / online
      // events so coming back to a tab that idled for minutes doesn't
      // wait out the watchdog interval before revalidating.
      forceReconnect() {
        if (stopped) return;
        killStaleSocket();
      },
      // Best-effort send on the current live socket (used by the latency
      // ping loop).  No-op when the socket is mid-reconnect.
      send(obj) {
        try {
          if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
        } catch (_) { /* socket raced closed */ }
      },
      lastFrameMs: () => lastFrameMs,
    };
  }

  window.openHubSocket = openHubSocket;
  window.createHubBridge = createHubBridge;
})();
